// stocai_frontend/src/components/HomePage/components/Journey/JourneySequence.jsx

import React, { useState, useEffect } from "react";
import AutoTimelineSequence from "../MilestoneSequence/AutoTimelineSequence";
import StakeholderCardWithData from "../widgets/Stakeholders/stakeholderValuesAndInterests/StakeholderCardWithData";
import MeetingPersonalityCard from "../widgets/Module4/MeetingPersonalityCard";
import ProfileCard from "../widgets/Identity/CurrentYou/ProfileCard";
import UnmetNeedsCard from "../widgets/UnmetNeeds/UnmetNeedsCard";
import StakeholderListCard from "../widgets/Stakeholders/StakeholderListCard";
import NarrativeCompletionCard from "../widgets/Module6/Steps Building Narrative/NarrativeCompletionCard";
import FuturePersonaCard from "../widgets/Module7/FuturePersonaCard";
import FutureIdentityCard from "../widgets/Identity/FutureYou/FutureIdentityCard";

/**
 * JourneySequence Component
 * 
 * Full-screen immersive timeline sequence showing the complete career journey.
 * Displays: Current State → Unmet Needs → Stakeholders → Narrative → Persona → Future Identity
 * 
 * @param {string} userId - User ID for fetching data
 * @param {function} onComplete - Callback when sequence completes
 * @param {object} selectedCard - Selected narrative card data
 * @param {object} futureData - Future identity data
 * @param {array} coreValues - Array of core values for future identity
 * @param {array} behaviours - Array of future behaviours
 * @param {array} beliefs - Array of future beliefs
 * @param {array} risks - Array of potential risks
 * @param {string} layoutPhase - Current layout phase (immersive/full/intro)
 */
const JourneySequence = ({
  userId,
  onComplete,
  selectedCard = null,
  futureData = {},
  coreValues = [],
  behaviours = [],
  beliefs = [],
  risks = [],
  layoutPhase = "immersive",
}) => {
  const [isLoading, setIsLoading] = useState(true);

  // Ensure futureData is always an object
  const safeFutureData = futureData || {};

  // Check if in immersive/full-screen mode
  const isImmersive = layoutPhase === "immersive" || layoutPhase === "full";

  // Loading effect
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // Define the sequence steps (based on working FutureSequence)
  const steps = [
    // Step 1: Current Identity ✅ Self-fetching
    {
      position: "left",
      circles: { left: "", center: "Future Identity", right: "Unmet Needs" },
      component: ProfileCard,
      props: { userId },
      duration: 5000,
    },

    // Step 2: Future Identity (first appearance)
    {
      position: "center",
      circles: { left: "Present Identity", center: "", right: "Unmet Needs" },
      component: FutureIdentityCard,
      props: {
        futureData: safeFutureData,
        userName: safeFutureData.userName || "",
        profileImage: safeFutureData.profileImageUrl || "",
        coreValues: [],  // Don't show values yet
        showCoreValues: false,
        currentInput: ""
      },
      duration: 5000,
    },

    // Step 3: Unmet Needs ✅ Self-fetching
    {
      position: "center",
      circles: { left: "Future Identity", center: "", right: "Stakeholders" },
      component: UnmetNeedsCard,
      props: { userId },
      duration: 5000,
    },

    // Step 4: Stakeholder List ✅ Self-fetching
    {
      position: "center",
      circles: { left: "Unmet Needs", center: "", right: "Stakeholder Values" },
      component: StakeholderListCard,
      props: { userId, mode: "api" },
      duration: 5000,
    },

    // Step 5: Stakeholder Values ✅ Self-fetching
    {
      position: "center",
      circles: { left: "Stakeholders", center: "", right: "Meeting Personality" },
      component: StakeholderCardWithData,
      props: { userId, stakeholderIndex: 1 },
      duration: 5000,
    },

    // Step 6: Meeting Personality ✅ Self-fetching
    {
      position: "center",
      circles: { left: "Stakeholder Values", center: "", right: "Narrative" },
      component: MeetingPersonalityCard,
      props: { mode: "api", userId },
      duration: 5000,
    },

    // Step 7: Narrative Completion (uses selectedCard data)
    {
      position: "center",
      circles: { left: "Meeting Personality", center: "", right: "Future Persona" },
      component: NarrativeCompletionCard,
      props: {
        heroTitle: selectedCard?.name || "My Narrative",
        heroSubtitle: selectedCard?.tagline || "",
        bullets: Array.isArray(selectedCard?.bullets) ? selectedCard.bullets : [],
      },
      duration: 5000,
    },

    // Step 8: Future Persona (uses behaviours, beliefs, risks)
    {
      position: "center",
      circles: { left: "Narrative", center: "", right: "Future Identity" },
      component: FuturePersonaCard,
      props: {
        behaviours: behaviours,
        beliefs: beliefs,
        risks: risks,
      },
      duration: 5000,
    },

    // Step 9: Future Identity (FINAL - with core values) ✅
    {
      position: "center",
      circles: { left: "Future Persona", center: "", right: "" },
      component: FutureIdentityCard,
      props: {
        futureData: safeFutureData,
        userName: safeFutureData.userName,
        profileImage: safeFutureData.profileImageUrl,
        currentInput: "",
        coreValues,
        showCoreValues: true,
      },
      duration: 5000,
      final: true,
    },
  ];

  // const FallbackCard = ({ title }) => (
  //   <div className="flex items-center justify-center h-full text-gray-400 text-2xl font-gotham italic">
  //     {title}
  //   </div>
  // );

  // Loading screen
  if (isLoading) {
    return (
      <div className="fixed inset-0 w-full h-screen flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-4 animate-fadeIn">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#6FB1AF]"></div>
          <p className="text-gray-600 text-sm font-medium font-gotham">
            Preparing your journey...
          </p>
        </div>
      </div>
    );
  }

  // Full-screen immersive view
  if (isImmersive) {
    return (
      <div className="fixed inset-0 w-full h-screen bg-white z-50 overflow-hidden">
        <div className="w-full h-full flex flex-col">
          {/* Header Section */}
          <div className="w-full px-12 pb-6">
            <h1 className="font-gotham font-medium text-[36px] text-black leading-tight mb-2">
              Your Journey So Far
            </h1>
            <p className="font-gotham font-normal text-[20px] text-gray-700 max-w-3xl">
              A walkthrough of the key tasks you've completed and how each step moved your career forward.
            </p>
          </div>

          {/* Timeline Section */}
          <div className="flex-1 w-full flex items-center justify-center px-12 pb-12">
            <div className="w-full max-w-7xl h-full animate-fadeIn">
              <AutoTimelineSequence 
                steps={steps} 
                onComplete={onComplete} 
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular embedded view (fallback)
  return (
    <div className="w-full h-full bg-white">
      <div className="w-full h-full flex flex-col relative">
        {/* Timeline Section */}
        <div className="flex-1 w-full flex items-center justify-center bg-white px-12 pb-12 absolute top-12">
          <div className="w-full max-w-7xl h-full animate-fadeIn">
            <AutoTimelineSequence 
              steps={steps} 
              onComplete={onComplete} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneySequence;